import { addressClass as AC, addressType, type FieldType, type IEventAddress, type IMapLocation } from '@/interfaces'

import { addToJSON } from './helper'

export class EventAddress implements IEventAddress {
	[key: string]: FieldType
	id: string
	active: boolean
	address1?: string
	address2?: string
	address3?: string
	city?: string
	state?: string
	zip?: string
	country?: string
	name: string
	startDate: Date
	endDate: Date
	type: addressType
	addressClass: AC = AC.Other
	isLegacy: boolean
	placeId?: string
	latitude?: number
	longitude?: number
	mapBoundsNorth?: number
	mapBoundsSouth?: number
	mapBoundsEast?: number
	mapBoundsWest?: number
	mapOverride?: boolean

	constructor({
		id = '',
		active = true,
		address1 = '',
		address2 = '',
		address3 = '',
		city = '',
		state = '0',
		zip = '',
		country = '0',
		name = '',
		startDate = new Date(),
		endDate = new Date(),
		type = addressType.Event,
		addressClass = AC.Other,
		isPrimary = false,
		isLegacy = false,
		placeId,
		latitude,
		longitude,
		mapBoundsNorth,
		mapBoundsSouth,
		mapBoundsEast,
		mapBoundsWest,
		mapOverride
	}: Partial<IEventAddress> = {}) {
		this.id = id
		this.active = active
		this.address1 = address1
		this.address2 = address2
		this.address3 = address3
		this.city = city
		this.state = state
		this.zip = zip
		this.country = country
		this.name = name
		this.startDate = startDate
		this.endDate = endDate
		this.type = type
		this.addressClass = addressClass
		this.isPrimary = isPrimary
		this.isLegacy = isLegacy
		this.placeId = placeId
		this.latitude = latitude
		this.longitude = longitude
		this.mapBoundsNorth = mapBoundsNorth
		this.mapBoundsSouth = mapBoundsSouth
		this.mapBoundsEast = mapBoundsEast
		this.mapBoundsWest = mapBoundsWest
		this.mapOverride = mapOverride
		addToJSON(EventAddress)
	}
	public get fullAddress(): { location: string; address: string } {
		const locArray = [this.address1, this.address2, this.address3, this.city, this.state ? 0 : '', this.zip]
		const output = locArray.filter((l) => l).join(', ')

		return { location: this.name || '', address: output }
	}
	public get isPrimary(): boolean {
		return this.addressClass === AC.Primary
	}
	public set isPrimary(value: boolean) {
		this.addressClass = value ? AC.Primary : AC.Other
	}

	public get mapBounds(): { north: number; south: number; east: number; west: number } | undefined {
		if (!this.mapBoundsNorth || !this.mapBoundsSouth || !this.mapBoundsEast || !this.mapBoundsWest) return undefined
		return {
			north: this.mapBoundsNorth,
			south: this.mapBoundsSouth,
			east: this.mapBoundsEast,
			west: this.mapBoundsWest
		}
	}
	public get mapCenter(): { lat: number; lng: number } | undefined {
		if (!this.latitude || !this.longitude) return undefined
		return { lat: this.latitude, lng: this.longitude }
	}
	get mapLocation(): IMapLocation | undefined {
		if (!this.latitude || !this.longitude) return undefined
		return {
			lat: this.latitude as number,
			lng: this.longitude as number,
			north: this.mapBoundsNorth as number,
			south: this.mapBoundsSouth as number,
			east: this.mapBoundsEast as number,
			west: this.mapBoundsWest as number,
			name: this.name,
			icon: 'event',
			primary: this.isPrimary
		}
	}
	updateLocation(place: google.maps.places.PlaceResult) {
		const getAddressComponent = (type: string, longName = true) =>
			place.address_components?.find((a) => a.types.includes(type))?.[longName ? 'long_name' : 'short_name']

		const placeId = place.place_id
		const streetNumber = getAddressComponent('street_number')
		const route = getAddressComponent('route')
		const city = getAddressComponent('locality')
		const state = getAddressComponent('administrative_area_level_1', false)
		const county = getAddressComponent('administrative_area_level_2', false)
		const zip = getAddressComponent('postal_code')
		const zipSuffix = getAddressComponent('postal_code_suffix')
		const country = getAddressComponent('country', false)

		this.placeId = placeId
		this.name = place.name || ''
		this.address1 = streetNumber || route ? `${streetNumber || ''} ${route || ''}`.trim() : undefined
		this.address3 = this.address1 ? county : undefined
		this.country = country || undefined
		this.city = city || undefined
		this.state = state || undefined
		this.zip = zip ? `${zip}${zipSuffix ? `-${zipSuffix}` : ''}` : undefined
		this.latitude = place.geometry?.location?.lat()
		this.longitude = place.geometry?.location?.lng()

		if (place.geometry?.viewport) {
			const bounds = place.geometry.viewport.toJSON()
			this.mapBoundsNorth = parseFloat(bounds.north.toFixed(7))
			this.mapBoundsSouth = parseFloat(bounds.south.toFixed(7))
			this.mapBoundsEast = parseFloat(bounds.east.toFixed(7))
			this.mapBoundsWest = parseFloat(bounds.west.toFixed(7))
		}

		// Mark as Google-sourced data (not manually overridden)
		// This should only be called when user actively performs a Google Places search
		this.mapOverride = false
	}

	toJSON(): string {
		// This will be overwritten by the helper function
		return ''
	}
}
